import { IFormFieldError } from '@thinknimble/tn-forms'
import { parseBackendErrors } from './errors'

/**
 * Maps a camelCased backend field name to the expense form field that owns its
 * `<ErrorsList>`. Only entries where the names differ are listed; everything
 * else falls through to the identical camelCase name.
 */
const BACKEND_FIELD_ALIASES: Record<string, string> = {
  name: '_name',
  amountCurrency: 'amount',
  organization: 'organizationRef',
  category: 'categoryRef',
  cashFlowType: 'cashFlowTypeRef',
  paymentAccount: 'paymentAccountRef',
  vendor: 'vendorRef',
  reimburseTo: 'reimburseToRef',
  transactionType: 'transactionTypeRef',
  reports: 'reportsRef',
}

/** Form fields that render an `<ErrorsList>` and can host backend errors. */
const KNOWN_FORM_FIELDS = new Set([
  '_name',
  'expenseDate',
  'amount',
  'organizationRef',
  'categoryRef',
  'cashFlowTypeRef',
  'paymentAccountRef',
  'vendorRef',
  'providedId',
  'reimburseToRef',
  'isReconciled',
  'taxYear',
  'transactionTypeRef',
  'personalUsePercentage',
  'milesDriven',
  'reportsRef',
  'notes',
])

export interface RoutedExpenseErrors {
  /** Backend messages keyed by form field name (only fields with an ErrorsList). */
  fieldErrors: Record<string, string[]>
  /** Combined general/banner message, or null when everything routed to a field. */
  generalError: string | null
}

/**
 * Route a backend error onto the expense form: field-specific messages are
 * keyed by form field name for their `<ErrorsList>`, and anything general or
 * targeting a field the form does not render is folded into `generalError` so
 * nothing is silently dropped.
 */
export function routeExpenseBackendErrors(error: unknown): RoutedExpenseErrors {
  const { fieldErrors, generalErrors } = parseBackendErrors(error)
  const routed: Record<string, string[]> = {}
  const general = [...generalErrors]

  for (const [backendKey, messages] of Object.entries(fieldErrors)) {
    const formField = BACKEND_FIELD_ALIASES[backendKey] ?? backendKey
    if (KNOWN_FORM_FIELDS.has(formField)) {
      routed[formField] = [...(routed[formField] ?? []), ...messages]
    } else {
      general.push(`${backendKey}: ${messages.join(', ')}`)
    }
  }

  return {
    fieldErrors: routed,
    generalError: general.length ? general.join('; ') : null,
  }
}

/** Build `IFormFieldError`s from backend messages for a field's `<ErrorsList>`. */
export function backendFieldErrorList(
  fieldErrors: Record<string, string[]>,
  formField: string,
): IFormFieldError[] {
  return (fieldErrors[formField] ?? []).map((message, idx) => ({
    code: `backend-${formField}-${idx}`,
    message,
  }))
}
